'use client';
import { resolveImageAlt } from '@eliadmin/core/src/utils/image-alt-utils';


import type { Blog } from '@eliadmin/core/src/api/blogs/blogs-types';
import { ROUTES } from '@eliadmin/core/src/constants/routes';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';
import { getTextFromHTML } from '@eliadmin/core/src/utils/misc-utils';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import Image from '@eliadmin/core/src/components/image/image-wrapped';
import Link from '@eliadmin/core/src/components/link';
import React, { useState } from 'react';
import { Heading } from '../ui/heading';
import { Paragraph } from '../ui/paragraph';
import { calculateReadingTime } from '../../utils/calculate-reading-time';
import { Button } from '../ui/button';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';

export type BlogCardProps = {
  blog: Blog;
};

export function BlogCard({ blog }: BlogCardProps) {
  const textContent = getTextFromHTML(blog.content);
  const locale = useLocaleStore(state => state.currentLocale);
  const { t } = useTranslations();

  const [isHovered, setIsHovered] = useState(false);

  let formattedDate: string;

  try {
    formattedDate = new Date(blog.created_at).toLocaleDateString(locale);
  } catch (error) {
    if (error instanceof RangeError) {
      formattedDate = new Date(blog.created_at).toLocaleDateString('en-US');
    } else {
      throw error;
    }
  }

  const hasCategories = blog.categories && blog.categories.length > 0;

  return (
    <Link
      prefetch={false}
      href={ROUTES.BLOG_DETAIL({ slug: blog.slug })}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      className={cn(
        'relative col-span-1 flex w-full flex-col items-start justify-start gap-4 overflow-hidden rounded-[4px]',
        'group h-[390px] transition-all sm:h-[424px] lg:h-[480px]',
      )}
    >
      {hasCategories && (
        <div className="absolute left-[18px] top-[18px] z-10 flex flex-wrap gap-2 sm:left-5 sm:top-5 lg:left-6 lg:top-6">
          {blog.categories?.map(category => (
            <p
              key={category.name}
              className="flex h-[30px] items-center rounded-[4px] bg-[#FAF6E5] px-3 text-xs font-semibold uppercase text-black"
            >
              {category.name}
            </p>
          ))}
        </div>
      )}

      {blog?.image?.url && <Image
        src={blog.image.url}
        alt={resolveImageAlt(blog?.image, blog?.name)}
        width={0}
        height={0}
        sizes="(min-width: 1024px) 384px, (min-width: 640px) 45vw, calc(100vw - 32px)"
        className={cn(
          'absolute left-0 top-0 h-[220px] w-full rounded-b-[4px] object-cover sm:h-[240px] lg:h-[290px]',
          'transition-all group-hover:h-full',
        )}
      />}

      <div className="absolute bottom-0 left-1/2 z-10 mt-[204px] flex w-full -translate-x-1/2 flex-col gap-6 pb-6 sm:mt-[224px] lg:mt-[306px]">
        <div className="flex flex-col gap-2 px-[18px] sm:px-5 lg:px-6">
          <div className="flex items-center gap-2">
            <Paragraph size="sm" className="transition-all group-hover:text-white">
              {formattedDate}
            </Paragraph>
            <Paragraph size="sm" className="transition-all group-hover:text-white">
              |
            </Paragraph>
            <Paragraph size="sm" className="transition-all group-hover:text-white">
              {calculateReadingTime(textContent)} min.
            </Paragraph>
          </div>

          <Heading
            tag="h3"
            title={blog.name}
            className={{
              title: cn(isHovered ? 'h5' : 'h6', 'line-clamp-2 transition-all group-hover:line-clamp-3 group-hover:text-white'),
            }}
          />
        </div>

        <div className="w-full px-[18px] sm:px-5 lg:px-6">
          <Button asChild className={cn('w-fit', 'group-hover:bg-white group-hover:text-black')}>
            <span>{t('text3391')}</span>
          </Button>
        </div>
      </div>

      <div
        className={cn(
          'ea-blog-card__hover-overlay',
          'absolute inset-0 z-[1] opacity-0',
          'transition-all group-hover:opacity-100',
        )}
      />
    </Link>
  );
}
